import {
  Avatar,
  Box,
  Chip,
  InputAdornment,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Database, Search } from "lucide-react";
import { useEffect, useState } from "react";
import PageHeader from "../../../common/components/PageHeader";
import { toast } from "../../../common/components/Toast";
import { SPACING } from "../../../common/constants/spacing";
import { truncateString } from "../../../common/utils/format_utils";
import { useSourcesList } from "../../api/admin";
import { StatCard } from "../../shared/components/StatCard";

function Sources() {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const pageSize = 20;

  // Debounce search term to avoid too many API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      // Reset to page 1 when search term changes
      if (searchTerm !== debouncedSearchTerm) {
        setCurrentPage(1);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm, debouncedSearchTerm]);

  const { sources, totalCount, totalPages, error } = useSourcesList(
    currentPage,
    pageSize,
    debouncedSearchTerm,
  );

  useEffect(() => {
    if (error) {
      toast.error("Failed to load sources");
      console.error("Error fetching sources:", error);
    }
  }, [error]);

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  const getPlatformColor = (platform) => {
    const platformColors = {
      youtube: "error",
      facebook: "primary",
      instagram: "secondary",
      tiktok: "info",
      twitch: "warning",
      vimeo: "success",
      google_ads: "default",
      amazon: "warning",
    };
    return platformColors[platform] || "default";
  };

  const getStatusColor = (status) => {
    return status === "active" ? "success" : "default";
  };

  const formatNumber = (num) => {
    if (!num) return "0";
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + "M";
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + "K";
    }
    return num.toString();
  };

  const getPlatformDisplayName = (platform) => {
    const platformNames = {
      youtube: "YouTube",
      facebook: "Facebook",
      instagram: "Instagram",
      tiktok: "TikTok",
      twitch: "Twitch",
      vimeo: "Vimeo",
      google_ads: "Google Ads",
      amazon: "Amazon",
    };
    return platformNames[platform] || platform;
  };

  return (
    <>
      <PageHeader
        title="Sources"
        description="Manage all sources across the platform. View source details, platforms, and performance metrics."
        appendActions={
          <Box sx={{ minWidth: 230 }}>
            <StatCard
              title="Total Sources"
              value={totalCount}
              icon={Database}
              color="#1976d2"
            />
          </Box>
        }
      />

      <TextField
        fullWidth
        placeholder="Search sources by name, platform, channel ID, or project..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search size={20} />
            </InputAdornment>
          ),
        }}
        sx={{ maxWidth: 500, mb: SPACING.sm }}
      />

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Source</TableCell>
              <TableCell>Platform</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Impressions</TableCell>
              <TableCell align="right">Followers</TableCell>
              <TableCell align="right">Likes</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Last Fetched</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sources.length > 0 ? (
              sources.map((source) => (
                <TableRow key={source.id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Avatar
                        src={source.avatar || source.thumbnail}
                        sx={{ width: 40, height: 40 }}
                      />
                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 500 }}
                        >
                          {source.account_name}
                        </Typography>
                        {source.channel_id && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ whiteSpace: "nowrap" }}
                          >
                            {truncateString(source.description, 35)}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getPlatformDisplayName(source.platform)}
                      color={getPlatformColor(source.platform)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={
                        source.status.charAt(0).toUpperCase() +
                        source.status.slice(1)
                      }
                      color={getStatusColor(source.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="bodySm">
                      {formatNumber(source.total_impressions)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="bodySm">
                      {formatNumber(source.follower_count)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="bodySm">
                      {formatNumber(source.likes_count)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="bodySm">
                      {formatDate(source.created_at)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="bodySm">
                      {formatDate(source.last_fetched_at)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={9} sx={{ textAlign: "center", py: 4 }}>
                  <Typography variant="h6" color="text.secondary">
                    {debouncedSearchTerm
                      ? "No sources found matching your search"
                      : "No sources found"}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={4}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            size="large"
            showFirstButton
            showLastButton
          />
        </Box>
      )}
    </>
  );
}

export default Sources;
